<script>
  import LocationsFilters from "@/components/LocationsFilter/LocationsFilters.vue";
  import { fetchOffices } from "@/services/fetchOffices";
  import policyLocations from "@/mixins/policyLocations";

  export default {
    components: {
      LocationsFilters,
    },
    mixins: [policyLocations],
    data() {
      return {
        locationsSelected: [],
        selectedCityNames: [],
        selectedOfficeNames: [],
        citiesData: [],
        officesData: [],
        room: "",
        available: "",
        platform: "",
        availableOptions: [
          { text: "Disponibilidad", value: "" },
          { text: "Disponible", value: "disponibles" },
          { text: "Ocupadas", value: "ocupadas" },
          { text: "Deshabilitada", value: "deshabilitadas" },
        ],
      };
    },
    computed: {
      query() {
        const query = {};
        if (this.selectedCityNames.length) {
          query.cities = this.selectedCityNames.join(",");
        }
        if (this.selectedOfficeNames.length) {
          query.offices = this.selectedOfficeNames.join(",");
        }
        if (this.room) {
          query.room = this.room;
        }
        if (this.available) {
          query.available = this.available;
        }
        if (this.platform) {
          query.platform = this.platform;
        }

        return query;
      },
      availableCities() {
        const provided = Array.isArray(this.citiesData) ? this.citiesData : [];
        const source = provided.length ? provided : (Array.isArray(this.$store.getters["cities/getData"]) ? this.$store.getters["cities/getData"] : []);
        const filtered = this.$filterCitiesByPolicy(source);
        return filtered.length ? filtered : null;
      },
      availableOffices() {
        const provided = Array.isArray(this.officesData) ? this.officesData : [];
        const source = provided.length ? provided : (Array.isArray(this.$store.getters["offices/getData"]) ? this.$store.getters["offices/getData"] : []);
        const filtered = this.$filterOfficesByPolicy(source);
        return filtered.length ? filtered : null;
      },
      rooms() {
        const roomsData = this.$store.getters["rooms/getData"];
        if (!Array.isArray(roomsData) || (!this.selectedCityNames.length && !this.selectedOfficeNames.length)) {
          return [];
        }

        if (this.selectedOfficeNames.length) {
          return roomsData.filter((room) => this.selectedOfficeNames.includes(room?.officeName));
        }

        return roomsData.filter((room) => this.selectedCityNames.includes(room?.cityName));
      },
      showClean() {
        return Boolean(this.selectedCityNames.length || this.selectedOfficeNames.length || this.room || this.available || this.platform);
      },
      hasLocationSelection() {
        return this.selectedCityNames.length > 0 || this.selectedOfficeNames.length > 0;
      },
      cities() {
        return this.$store.state.cities.data;
      },
    },
    watch: {
      $route(to) {
        if (this.queriesMatch(to.query)) return;
        this.hydrateFromRoute(to.query);
      },
      availableCities(newVal, oldVal) {
        const hadData = Array.isArray(oldVal) && oldVal.length > 0;
        const hasData = Array.isArray(newVal) && newVal.length > 0;

        if (!hadData && hasData) {
          this.hydrateFromRoute();
        }
      },
      availableOffices(newVal, oldVal) {
        const hadData = Array.isArray(oldVal) && oldVal.length > 0;
        const hasData = Array.isArray(newVal) && newVal.length > 0;

        if (!hadData && hasData) {
          this.hydrateFromRoute();
        }
      },
    },
    methods: {
      parseQueryList(value) {
        if (!value) return [];
        const list = Array.isArray(value) ? value : [value];
        const parsed = list
          .flatMap((entry) => String(entry).split(","))
          .map((entry) => entry.trim())
          .filter(Boolean);
        return [...new Set(parsed)];
      },
      buildLocationSelection(cityNames, officeNames) {
        const citiesSource = Array.isArray(this.availableCities) ? this.availableCities : [];
        const officesSource = Array.isArray(this.availableOffices) ? this.availableOffices : [];

        if (!citiesSource.length && !officesSource.length) {
          this.locationsSelected = [];
          return;
        }

        const normalize = (item) => ({
          ...item,
          name: item?.officeName || item?.cityName,
        });

        const normalizeValue = (value) =>
          String(value || "")
            .toLowerCase()
            .trim();

        const cityTargets = cityNames.map(normalizeValue);
        const officeTargets = officeNames.map(normalizeValue);

        const selectedCities = citiesSource
          .filter((city) => {
            const candidates = [city?.cityName, city?.name, city?.Nombre];
            return candidates.some((candidate) => cityTargets.includes(normalizeValue(candidate)));
          })
          .map(normalize);

        const selectedOffices = officesSource
          .filter((office) => {
            const candidates = [office?.officeName, office?.name];
            return candidates.some((candidate) => officeTargets.includes(normalizeValue(candidate)));
          })
          .map(normalize);

        this.locationsSelected = [...selectedCities, ...selectedOffices];
      },
      hydrateFromRoute(routeQuery = this.$route.query) {
        this.room = routeQuery.room || "";
        this.available = routeQuery.available || "";
        this.platform = routeQuery.platform || "";

        const cityNames = this.parseQueryList(routeQuery.cities);
        const officeNames = this.parseQueryList(routeQuery.offices);

        this.selectedCityNames = cityNames;
        this.selectedOfficeNames = officeNames;

        this.buildLocationSelection(cityNames, officeNames);
      },
      queriesMatch(routeQuery = {}) {
        const current = this.query;

        const normalizeList = (value) => this.parseQueryList(value).join(",");

        const sameCities = normalizeList(routeQuery.cities) === normalizeList(current.cities);
        const sameOffices = normalizeList(routeQuery.offices) === normalizeList(current.offices);
        const sameRoom = (routeQuery.room || "") === (current.room || "");
        const sameAvailable = (routeQuery.available || "") === (current.available || "");
        const samePlatform = (routeQuery.platform || "") === (current.platform || "");

        return sameCities && sameOffices && sameRoom && sameAvailable && samePlatform;
      },
      async initLocationsData() {
        try {
          const [cities, offices] = await Promise.all([this.cities, fetchOffices()]);
          this.citiesData = Array.isArray(cities) ? cities : [];
          this.officesData = Array.isArray(offices) ? offices : [];
        } catch (error) {
          this.citiesData = [];
          this.officesData = [];
        }

        this.hydrateFromRoute();
      },
      handleLocationsChange(locations) {
        this.locationsSelected = locations;
        this.selectedCityNames = locations
          .filter((loc) => !loc?.cityId)
          .map((city) => city?.cityName || city?.name)
          .filter(Boolean);
        this.selectedOfficeNames = locations
          .filter((loc) => loc?.cityId)
          .map((office) => office?.officeName || office?.name)
          .filter(Boolean);

        this.executeFilter("locations");
      },
      executeFilter(key) {
        const cleanByKey = {
          locations: () => {
            this.room = "";
          },
          reset: () => {
            this.locationsSelected = [];
            this.selectedCityNames = [];
            this.selectedOfficeNames = [];
            this.room = "";
            this.available = "";
            this.platform = "";
          },
          available: () => (this.platform = ""),
          platform: () => (this.available = ""),
        };
        cleanByKey?.[key]?.();
        this.$router.push({ query: this.query }).catch(() => {});
      },
    },
    async mounted() {
      await this.initLocationsData();
      if (this.$policyAllowedCityNames && !this.$route.query.cities && !this.$route.query.offices) {
        const payload = this.$policyLocationsPayload;
        const cityNames = [];
        const officeNames = [];
        Object.entries(payload).forEach(([cityName, offices]) => {
          if (offices.length) officeNames.push(...offices);
          else cityNames.push(cityName);
        });
        const query = { ...this.$route.query };
        if (cityNames.length) query.cities = cityNames.join(",");
        if (officeNames.length) query.offices = officeNames.join(",");
        this.$router.replace({ query }).catch(() => {});
      }
    },
  };
</script>

<template>
  <section class="filtersInLive">
    <div class="filtersInLive__filters">
      <LocationsFilters :locations-selected="locationsSelected" :cities="availableCities" :offices="availableOffices" :disabled="$policyIsLocked" @upd-locations="handleLocationsChange" />
      <div class="filtersInLive__filterSelect">
        <iconic class="filtersInLive__filterIcon" name="filter"></iconic>
        <select class="filtersInLive__filterItem" name="room" :disabled="!hasLocationSelection" v-model="room" @change="executeFilter">
          <option value="" selected>Habitación</option>
          <option :value="roomData.roomName" v-for="roomData in rooms" :key="roomData._id">{{ roomData.roomName }}</option>
        </select>
      </div>
      <div class="filtersInLive__filterSelect">
        <iconic class="filtersInLive__filterIcon" name="filter"></iconic>
        <select class="filtersInLive__filterItem" name="available" v-model="available" @change="executeFilter('available')">
          <option :value="available.value" v-for="(available, idx) in availableOptions" :key="idx">{{ available.text }}</option>
        </select>
      </div>
      <div :class="['filtersInLive__filtersClean', { 'filtersInLive__filtersClean--disabled': $policyIsLocked }]" @click="!$policyIsLocked && executeFilter('reset')" v-if="showClean">
        <i class="fa fa-times" aria-hidden="true"></i>
        <p>Limpiar Filtros</p>
      </div>
    </div>
  </section>
</template>

<style lang="scss">
  .filtersInLive {
    @include Flex(column);
    gap: 10px;
    width: 100%;
    padding: $mpadding;
    background: white;
    box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.16);
    border-radius: 6px;
    &__filters {
      @include Flex(column);
      gap: 10px;
      width: 100%;
    }
    &__filtersClean {
      padding: 0 15px;
      font-size: 16px;
      font-weight: 600;
      color: $primary-color;
      cursor: pointer;
      width: auto;
      display: flex;
      align-items: center;
      gap: 10px;
      p {
        width: 120px;
      }
      &--disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
    }
    &__filterSelect {
      font-size: 16px;
      position: relative;
      width: 100%;
    }
    &__filterIcon {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 15px;
      margin: auto;
    }
    &__filterItem {
      width: 100%;
      height: 36px;
      padding-left: $mpadding * 2.5;
      appearance: none;
      text-align: start;
      text-transform: capitalize;
      outline: none;
      color: $black;
      background: #f0f0f0;
      box-shadow: 0px 0.5px 2px rgba(0, 0, 0, 0.41);
      border: none;
      border-radius: 4px;
      cursor: pointer;
    }
    @media screen and (min-width: $tablet-width) {
      &__filters {
        @include Flex(row, space-between);
      }
    }
  }
</style>
